import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { createBooking, checkAvailability } from '../../utils/supabaseExtended';
import { calculateBookingCosts } from '../../utils/stripeConfig';
import { useAuth } from '../../utils/AuthContext';
import Button from './Button';
import { CheckIcon, ClockIcon } from '../../utils/IconWrapper';
import AvailabilityCalendar from './AvailabilityCalendar';

const BookingContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid #e1e5e9;
`;

const FormSection = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s;

  &:focus {
    border-color: #0d9488;
    outline: none;
  }

  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }
`;

const DateGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const CalendarSection = styled.div`
  margin: 1.5rem 0;
`;

const DateSummary = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f0fdfa;
  border-radius: 8px;
  border: 2px solid #0d9488;
  margin-bottom: 1rem;
  
  .date-item {
    text-align: center;
    
    .label {
      font-size: 0.8rem;
      color: #666;
      margin-bottom: 0.25rem;
    }
    
    .date {
      font-weight: 600;
      color: #0d9488;
      font-size: 0.9rem;
    }
  }
  
  .nights {
    text-align: center;
    font-weight: 600;
    color: #0d9488;
    
    .number {
      font-size: 1.5rem;
      display: block;
    }
    
    .text {
      font-size: 0.8rem;
    }
  }
`;

const PricingBreakdown = styled.div`
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  margin: 1.5rem 0;
`;

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  
  &.total {
    font-weight: bold;
    font-size: 1.2rem;
    border-top: 1px solid #dee2e6;
    padding-top: 0.5rem;
    margin-top: 1rem;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const SuccessMessage = styled.div`
  color: #28a745;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #0d9488;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

interface BookingFormProps {
  accommodationId: string;
  accommodationTitle: string;
  basePrice: number;
  maxGuests: number;
}

const BookingFormComponent: React.FC<BookingFormProps> = ({
  accommodationId,
  accommodationTitle,
  basePrice,
  maxGuests
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Form state
  const [dateRange, setDateRange] = useState<{
    checkIn: string | null;
    checkOut: string | null;
  }>({ checkIn: null, checkOut: null });
  const [guests, setGuests] = useState(1);
  const [guestName, setGuestName] = useState(user?.email?.split('@')[0] || '');
  const [guestEmail, setGuestEmail] = useState(user?.email || '');
  const [guestPhone, setGuestPhone] = useState('');
  const [specialRequests, setSpecialRequests] = useState('');

  // Pricing
  const [pricing, setPricing] = useState<any>(null);
  const [nights, setNights] = useState(0);

  // Status
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [availability, setAvailability] = useState<{ available: boolean; message?: string }>({ available: true });

  // Calculate pricing when dates change
  useEffect(() => {
    if (dateRange.checkIn && dateRange.checkOut) {
      const startDate = new Date(dateRange.checkIn);
      const endDate = new Date(dateRange.checkOut);
      const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
      const calculatedNights = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (calculatedNights > 0) {
        setNights(calculatedNights);
        const pricingCalc = calculateBookingCosts(basePrice, calculatedNights);
        setPricing(pricingCalc);
      }
    } else {
      setNights(0);
      setPricing(null);
    }
  }, [dateRange.checkIn, dateRange.checkOut, basePrice]);

  // Check availability when dates change
  useEffect(() => {
    const checkDatesAvailability = async () => {
      if (dateRange.checkIn && dateRange.checkOut) {
        try {
          const result = await checkAvailability(accommodationId, dateRange.checkIn, dateRange.checkOut);
          setAvailability(result);
        } catch (error) {
          setAvailability({ available: false, message: 'Errore nel controllo disponibilità' });
        }
      } else {
        setAvailability({ available: true });
      }
    };

    checkDatesAvailability();
  }, [dateRange.checkIn, dateRange.checkOut, accommodationId]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!user) {
      setError('Devi essere autenticato per prenotare');
      return;
    }

    if (!dateRange.checkIn || !dateRange.checkOut) {
      setError('Seleziona entrambe le date per continuare');
      return;
    }

    if (!availability.available) {
      setError('Date selezionate non disponibili');
      return;
    }

    if (!pricing) {
      setError('Errore nel calcolo del prezzo');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Crea la prenotazione (senza pagamento)
      const bookingResult = await createBooking({
        accommodationId,
        checkIn: dateRange.checkIn!,
        checkOut: dateRange.checkOut!,
        guestsCount: guests,
        guestName,
        guestEmail,
        guestPhone,
        specialRequests
      });

      if (!bookingResult.success || !bookingResult.booking) {
        throw new Error(bookingResult.error || 'Errore nella creazione della prenotazione');
      }

      // Reindirizza alla pagina di checkout con i dati della prenotazione
      navigate('/checkout', {
        state: {
          bookingData: {
            bookingId: bookingResult.booking.id,
            accommodationId,
            accommodationTitle,
            checkIn: dateRange.checkIn!,
            checkOut: dateRange.checkOut!,
            guestsCount: guests,
            guestName,
            guestEmail,
            guestPhone,
            specialRequests,
            basePrice,
            nights,
            ...pricing
          }
        }
      });

    } catch (error: any) {
      setError(error.message || 'Errore durante la prenotazione');
      setIsLoading(false);
    }
  };

  // Get today's date for min attribute
  const today = new Date().toISOString().split('T')[0];

  // Helper function to format dates
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', { 
      weekday: 'short',
      day: 'numeric', 
      month: 'short' 
    });
  };

  const handleDateRangeSelect = (checkIn: string, checkOut: string) => {
    setDateRange({ checkIn, checkOut });
  };

  return (
    <BookingContainer>
      <h3 style={{ marginBottom: '1.5rem', color: '#333' }}>
        Prenota "{accommodationTitle}"
      </h3>

      <form onSubmit={handleSubmit}>
        <CalendarSection>
          <Label>Seleziona Date</Label>
          
          {dateRange.checkIn && dateRange.checkOut && (
            <DateSummary>
              <div className="date-item">
                <div className="label">Check-in</div>
                <div className="date">{formatDate(dateRange.checkIn)}</div>
              </div>
              <div className="nights">
                <span className="number">{nights}</span>
                <span className="text">nott{nights === 1 ? 'e' : 'i'}</span>
              </div>
              <div className="date-item">
                <div className="label">Check-out</div>
                <div className="date">{formatDate(dateRange.checkOut)}</div>
              </div>
            </DateSummary>
          )}
          
          <AvailabilityCalendar
            accommodationId={accommodationId}
            selectedRange={dateRange}
            onDateSelect={handleDateRangeSelect}
            minDate={today}
          />
          
          {!availability.available && availability.message && (
            <ErrorMessage>
              ⚠️ {availability.message}
            </ErrorMessage>
          )}
        </CalendarSection>

        <FormSection>
          <Label>Numero di Ospiti</Label>
          <Input
            type="number"
            value={guests}
            onChange={(e) => setGuests(parseInt(e.target.value))}
            min="1"
            max={maxGuests}
            required
          />
        </FormSection>

        <FormSection>
          <Label>Nome Completo</Label>
          <Input
            type="text"
            value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
            required
          />
        </FormSection>

        <FormSection>
          <Label>Email</Label>
          <Input
            type="email"
            value={guestEmail}
            onChange={(e) => setGuestEmail(e.target.value)}
            required
          />
        </FormSection>

        <FormSection>
          <Label>Telefono (opzionale)</Label>
          <Input
            type="tel"
            value={guestPhone}
            onChange={(e) => setGuestPhone(e.target.value)}
          />
        </FormSection>

        <FormSection>
          <Label>Richieste Speciali (opzionale)</Label>
          <Input
            as="textarea"
            value={specialRequests}
            onChange={(e) => setSpecialRequests(e.target.value)}
            rows={3}
            style={{ resize: 'vertical', minHeight: '80px' }}
          />
        </FormSection>

        {/* Availability Status */}
        {dateRange.checkIn && dateRange.checkOut && (
          <div style={{ margin: '1rem 0' }}>
            {availability.available ? (
              <SuccessMessage>
                <CheckIcon style={{ marginRight: '0.5rem' }} />
                Date disponibili
              </SuccessMessage>
            ) : (
              <ErrorMessage>
                <ClockIcon style={{ marginRight: '0.5rem' }} />
                {availability.message || 'Date non disponibili'}
              </ErrorMessage>
            )}
          </div>
        )}

        {/* Pricing Breakdown */}
        {pricing && nights > 0 && (
          <PricingBreakdown>
            <h4 style={{ marginBottom: '1rem' }}>Riepilogo Prezzi</h4>
            <PriceRow>
              <span>€{basePrice} × {nights} notti</span>
              <span>€{pricing.subtotal.toFixed(2)}</span>
            </PriceRow>
            <PriceRow>
              <span>Pulizie</span>
              <span>€{pricing.cleaningFee.toFixed(2)}</span>
            </PriceRow>
            <PriceRow>
              <span>Commissioni servizio</span>
              <span>€{pricing.serviceFee.toFixed(2)}</span>
            </PriceRow>
            <PriceRow>
              <span>Tasse</span>
              <span>€{pricing.taxes.toFixed(2)}</span>
            </PriceRow>
            <PriceRow className="total">
              <span>Totale</span>
              <span>€{pricing.total.toFixed(2)}</span>
            </PriceRow>
          </PricingBreakdown>
        )}

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <Button
          type="submit"
          disabled={isLoading || !availability.available || !pricing || !user}
          style={{
            width: '100%',
            marginTop: '1rem',
            backgroundColor: '#0d9488',
            padding: '1rem'
          }}
        >
          {isLoading ? (
            <>
              <LoadingSpinner style={{ marginRight: '0.5rem' }} />
              Creazione prenotazione...
            </>
          ) : (
            `Procedi al Pagamento - €${pricing?.total.toFixed(2) || '0.00'}`
          )}
        </Button>
      </form>
    </BookingContainer>
  );
};

const BookingForm: React.FC<BookingFormProps> = (props) => {
  return <BookingFormComponent {...props} />;
};

export default BookingForm; 